import { useTranslation } from 'react-i18next';
import { RotateCcw, Search, XCircle } from 'lucide-react';
import { useExpiryCountdown } from '../../hooks/useExpiryCountdown';
import { log } from '../../utils/logger';
/*
 * Il punteggio dell'incontro si scrive con `puntiTorneo`, il formatter delle
 * classifiche: sulla scala 2 / 1 / 0 (`MATCH_SCORING`) ogni conto e' un intero,
 * e la copia locale che stava qui — che faceva `punti - interi >= 0.5` — avrebbe
 * scritto `1½` per un `1.7`, cioe' un numero falso. Quello riconosce il mezzo
 * ESATTO e per tutto il resto ricade su `decimale()`, col separatore giusto
 * nelle otto lingue.
 */
import { puntiTorneo } from '../../utils/tournament-helpers';
import IncomingRematchPrompt from './IncomingRematchPrompt';
import '../shared/offer-bar.css';
import './RematchPanel.css';

interface RematchPanelProps {
  sessionScore: { white: number; black: number };
  playerColor: 'white' | 'black';
  opponentWantsRematch: boolean;
  isRequestingRematch: boolean;
  canRematch: boolean;
  /** Aggregate score of a finished two-leg match (null for single games). */
  matchScore?: { you: number; opponent: number } | null;
  /** True once both legs of the match have been played. */
  matchComplete?: boolean;
  /** Server deadline (epoch ms) for the offer this player sent; null when none. */
  offerExpiresAt?: number | null;
  /**
   * Come e' finita la proposta, se e' finita. 'declined' e 'expired' NON sono la
   * stessa cosa: dopo un rifiuto il server cancella la stanza e non c'e' piu'
   * niente da proporre, dopo una scadenza la stanza c'e' ancora e si puo'
   * riprovare — ed e' `canRematch` a dirlo.
   */
  notice?: 'declined' | 'expired' | null;
  onRequestRematch: () => void;
  onDeclineRematch: () => void;
  onNewGame: () => void;
}

function RematchPanel({
  sessionScore,
  playerColor,
  opponentWantsRematch,
  isRequestingRematch,
  canRematch,
  matchScore = null,
  matchComplete = false,
  offerExpiresAt = null,
  notice = null,
  onRequestRematch,
  onDeclineRematch,
  onNewGame,
}: RematchPanelProps) {
  const { t } = useTranslation();
  // Il numero e' solo il riflesso del timer che il server ha gia' armato: alla
  // scadenza vera arriva `rematch:expired`, che e' quello che cambia lo stato.
  const secondsLeft = useExpiryCountdown(isRequestingRematch ? offerExpiresAt : null);

  const yourScore = playerColor === 'white' ? sessionScore.white : sessionScore.black;
  const opponentScore = playerColor === 'white' ? sessionScore.black : sessionScore.white;

  // Una sfida andata e ritorno è un match: giocate entrambe le manche si mostra
  // il totale e si offre un match NUOVO (col sorteggio, una pescata nuova)
  // invece di una semplice «rivincita».
  const showMatchResult = matchComplete && matchScore !== null;

  return (
    <div className="rematch-panel">
      {showMatchResult && (
        <div className="match-result" data-testid="match-result">
          <span className="match-result-label">{t('online.match.complete')}</span>
          <span className="match-result-score">
            {puntiTorneo(matchScore.you)} - {puntiTorneo(matchScore.opponent)}
          </span>
        </div>
      )}

      {/* Session Score */}
      {(sessionScore.white > 0 || sessionScore.black > 0) && (
        <div className="session-score">
          <span className="score-label">{t('online.rematch.sessionScore')}:</span>
          <span className="score-value">
            {yourScore} - {opponentScore}
          </span>
        </div>
      )}

      {/* Esito della proposta appena chiusa. Prima non c'era: il bottone
          Rivincita spariva e basta, e nessuno capiva perche'. */}
      {notice && !opponentWantsRematch && !isRequestingRematch && (
        <div className="offer-chip rematch-notice" data-testid="rematch-notice">
          <XCircle size={16} aria-hidden="true" />
          <span>{t(`online.rematch.${notice}`)}</span>
        </div>
      )}

      {isRequestingRematch && !opponentWantsRematch && (
        <div className="rematch-waiting">
          <div className="spinner-small" />
          <span>{t('online.rematch.waitingForOpponent')}</span>
          {offerExpiresAt !== null && (
            <span className="rematch-timer" data-testid="rematch-timer">{secondsLeft}s</span>
          )}
        </div>
      )}

      {/* La proposta in arrivo porta con se' i propri bottoni: e' lo stesso
          blocco montato anche dentro GameOverOverlay, dove chi la riceve sta
          quasi sempre guardando. */}
      {opponentWantsRematch && canRematch ? (
        <IncomingRematchPrompt onAccept={onRequestRematch} onDecline={onDeclineRematch} />
      ) : (
        /* Action Buttons — neutral icon+label bar, matching the in-game toolbar */
        <div className="rematch-actions">
          {/* In attesa il bottone resta se stesso, solo spento: prima si svuotava
              per lasciare il posto a una rotellina quasi invisibile, e mezza barra
              sembrava rotta. Lo stato "sto aspettando" ora lo dice la riga sopra,
              con la sua rotellina e il conto alla rovescia — qui basta che il
              bottone non sia piu' premibile. */}
          {canRematch && !opponentWantsRematch && (
            <button
              className="rematch-btn request"
              onClick={() => {
                log.info('RematchPanel', 'Rematch requested');
                onRequestRematch();
              }}
              disabled={isRequestingRematch}
            >
              <RotateCcw size={20} aria-hidden="true" />
              <span>{showMatchResult ? t('online.match.newMatch') : t('online.rematch.rematch')}</span>
            </button>
          )}

          {!opponentWantsRematch && (
            <button className="new-game-btn" onClick={onNewGame}>
              <Search size={20} aria-hidden="true" />
              <span>{t('online.rematch.newOpponent')}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default RematchPanel;
